import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import { PermissionGroup } from '@interfaces/a-organizar/permissionGroup';
import { isHiddenRole } from '@app/features/dashboard/group-register/permission-tree/permission-labels';
import { describeRemovalImpact } from './group-list.utils';
import { ConfirmationService } from '@src/app/core/services/helpers/confirmation.service';
import { PermissionsService } from '@services/helpers/permissions.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { UtilsService } from '@services/helpers/utils.service';
import { UserService } from '@services/http/user.service';
import { SharedModule } from '@shared/shared.module';
import { forkJoin, map, of, switchMap } from 'rxjs';
import { GroupListHeaderComponent } from './components/group-list-header/group-list-header.component';
import { GroupListRowComponent } from './components/group-list-row/group-list-row.component';
import { GroupRoleChipsComponent } from './components/group-role-chips/group-role-chips.component';
import {
  GroupListRow,
  RoleCatalogIndex,
  buildRoleChips,
  buildRow,
  filterRows,
  sortRows,
} from './group-list.utils';
import { RoleCatalogService } from './services/role-catalog.service';

/** Os grupos são poucos (dezenas): vêm todos numa chamada e o filtro, a busca e as contagens rodam na tela. */
export const GROUP_LIST_PAGE_SIZE = 500;

/**
 * Lista de grupos de permissão (card 3902, opção A do protótipo, com Editar e Excluir no menu ⋯ da linha).
 * Filtro por tipo de usuário em chips, busca pelo nome e, por grupo, os módulos do menu que ele libera, leitura x
 * gravação e quantos usuários estão nele. Regras no README da pasta.
 */
@Component({
  selector: 'app-group-list',
  standalone: true,
  imports: [SharedModule, GroupListHeaderComponent, GroupRoleChipsComponent, GroupListRowComponent],
  templateUrl: './group-list.component.html',
  styleUrls: ['./group-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GroupListComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly roleCatalog = inject(RoleCatalogService);
  private readonly permissionsService = inject(PermissionsService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly toast = inject(ToastHelperService);
  private readonly utilsService = inject(UtilsService);
  private readonly destroyRef = inject(DestroyRef);

  readonly groupPermissions = Permissions.PermissionGroup;

  readonly canCreate = toSignal(
    this.permissionsService.hasPermission(this.groupPermissions.CREATE),
    {
      initialValue: false,
    }
  );
  readonly canUpdate = toSignal(
    this.permissionsService.hasPermission(this.groupPermissions.UPDATE),
    {
      initialValue: false,
    }
  );
  readonly canDelete = toSignal(
    this.permissionsService.hasPermission(this.groupPermissions.DELETE),
    {
      initialValue: false,
    }
  );

  /** Primeira carga (ou nova tentativa depois de erro): mostra o carregando no lugar da tabela. */
  readonly loading = signal(true);
  readonly loadError = signal(false);
  /** O backend tem mais grupos que os que vieram (`GROUP_LIST_PAGE_SIZE`). */
  readonly totalFromServer = signal(0);
  readonly rows = signal<GroupListRow[]>([]);
  readonly query = signal('');
  readonly roleFilter = signal('');

  readonly chips = computed(() => buildRoleChips(this.rows()));
  readonly visibleRows = computed(() => filterRows(this.rows(), this.roleFilter(), this.query()));
  readonly hasGroups = computed(() => this.rows().length > 0);
  readonly isTruncated = computed(() => this.totalFromServer() > this.rows().length);
  readonly footerText = computed(() => {
    const count = this.rows().length;
    const shown = this.visibleRows().length;
    const total = count === 1 ? '1 grupo' : `${count} grupos`;
    // Com filtro ou busca, diz quantos aparecem de quantos existem.
    return shown === count ? `${total}.` : `${shown} de ${total}.`;
  });
  readonly emptyFilterText = computed(() => {
    const query = this.query().trim();
    const chip = this.chips().find(item => item.roleId === this.roleFilter());
    const where = this.roleFilter() && chip ? ` do tipo ${chip.label}` : '';
    return query
      ? `Nenhum grupo${where} com "${query}" no nome.`
      : `Nenhum grupo${where} para mostrar.`;
  });

  ngOnInit(): void {
    this.loadGroups();
  }

  /** Busca os grupos e, para cada tipo de usuário presente, o catálogo dele (uma chamada por tipo, com cache). */
  loadGroups(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    this.loadError.set(false);

    this.userService
      .getPermissionsGroups('', { Page: 1, PageSize: GROUP_LIST_PAGE_SIZE }, '')
      .pipe(
        switchMap(response => {
          // Papel legado (CUSTOMER) fica fora: ao lado de "Empresa" só confunde (Victor, 04/11/2026).
          const received: PermissionGroup[] = response?.data ?? [];
          const groups = received.filter(group => !isHiddenRole(group.role?.name));
          // O total do servidor também conta os grupos escondidos: desconta os que vieram e foram tirados, senão o
          // aviso de lista cortada aparece sem a lista estar cortada.
          const serverTotal = Number(
            response?.total ?? response?.pagination?.total ?? received.length
          );
          const total = serverTotal - (received.length - groups.length);
          const roleIds = [...new Set(groups.map(group => group.roleId).filter(Boolean))];
          const indexes$ = roleIds.length
            ? forkJoin(roleIds.map(roleId => this.roleCatalog.getIndex(roleId)))
            : of([] as (RoleCatalogIndex | null)[]);
          return indexes$.pipe(
            map(indexes => {
              const byRole = new Map(roleIds.map((roleId, i) => [roleId, indexes[i]]));
              return {
                total,
                rows: sortRows(
                  groups.map(group => buildRow(group, byRole.get(group.roleId) ?? null))
                ),
              };
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: ({ total, rows }) => {
          this.rows.set(rows);
          this.totalFromServer.set(total);
          this.loading.set(false);
          // O tipo escolhido pode ter sumido (último grupo dele excluído): volta para Todos.
          if (this.roleFilter() && !rows.some(row => row.roleId === this.roleFilter())) {
            this.roleFilter.set('');
          }
        },
        error: () => {
          this.loading.set(false);
          this.loadError.set(true);
        },
      });
  }

  onQueryChange(query: string): void {
    this.query.set(query ?? '');
  }

  clearSearch(): void {
    this.query.set('');
  }

  addGroup(): void {
    this.router.navigate(['dashboard/groups/register']);
  }

  confirmRemove(row: GroupListRow): void {
    this.confirmation
      .confirm(
        `Excluir o grupo "${row.group.name}"?`,
        `${describeRemovalImpact(row.usersCount)} Não dá para desfazer.`,
        'danger',
        'Cancelar',
        'Excluir grupo'
      )
      .subscribe(confirmed => {
        if (confirmed) this.remove(row);
      });
  }

  private remove(row: GroupListRow): void {
    this.utilsService.setIsLoading(true);
    this.userService.deleteUserGroup(row.group.id).subscribe({
      next: () => {
        this.utilsService.setIsLoading(false);
        this.toast.showToastSuccess('Grupo excluído.', `"${row.group.name}" saiu da lista.`);
        this.loadGroups(false);
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        this.toast.showToastError(
          'Não foi possível excluir o grupo.',
          error?.errors?.length
            ? error.errors[0].message
            : 'Confira a conexão e tente de novo pelo menu ⋯ da linha. Nada foi apagado.'
        );
      },
    });
  }
}
